import { ChangeEvent, useState, useEffect } from "react";

import { useMsal } from "@azure/msal-react";
import moment from "moment";

import DialogMessage from "../../../components/DialogMessage";
import DialogMessageProps from "../../../model/DialogMessageProps";
import IModalProps from "../../../model/ModalProps";
import ActivityManagementDateService from "../../../service/Docente/ActivityManagementDateService";
import GenericUtils from "../../../utils/GenericUtils";

import InputCalendarStyles from "../../../styles/components/InputCalendarStyles";
import ButtonStyles from "../../../styles/components/ButtonStyles";
import DialogStyles from "../../../styles/components/DialogStyles";
import InputCalendarGroup from "./style/StylesGestaoAtividades";

interface IModalGestaoAtividadesProps extends IModalProps {
    open: boolean;
    setOpenDialogQuestion: (value: boolean) => void;
    selectedDate?: string;
    onSuccess: (e: any) => void;
    gestaoAtividadesEdit?: any;
}

interface IActivityManagementDate {
    referenceStartDate: Date | null;
    referenceEndDate: Date | null;
    justificationEndDate: Date | null;
    approvalEndDate: Date | null;
}

const initialActivityManagementDate: IActivityManagementDate = {
    referenceStartDate: null,
    referenceEndDate: null,
    justificationEndDate: null,
    approvalEndDate: null,
};

const ModalGestaoAtividadesContent: React.FC<IModalGestaoAtividadesProps> = ({
    setOpenDialogQuestion,
    selectedDate,
    onSuccess,
    gestaoAtividadesEdit,
}) => {
    const [activityManagementDate, setActivityManagementDate] =
        useState<IActivityManagementDate>(initialActivityManagementDate);

    const [dialogMessageProps, setDialogMessageProps] =
        useState<DialogMessageProps>({} as DialogMessageProps);

    const { BtnPrimary, DefaultButtonLink } = ButtonStyles;
    const { StyledContainerButton } = DialogStyles;

    const { instance } = useMsal();
    const dateFormat = "YYYY-MM-DD";

    const isEditableForm =
        gestaoAtividadesEdit && gestaoAtividadesEdit.isLoaded;

    const handleChangeActivityManagementDate = (
        event: ChangeEvent<HTMLInputElement>
    ) => {
        let newActivityManagementDate: IActivityManagementDate =
            initialActivityManagementDate;

        const value = event.target.value ? new Date(event.target.value) : null;

        if (event.target.name === "justificationEndDate") {
            newActivityManagementDate = {
                ...activityManagementDate,
                approvalEndDate: null,
                [event.target.name]: value,
            };
        } else {
            newActivityManagementDate = {
                ...activityManagementDate,
                [event.target.name]: value,
            };
        }

        setActivityManagementDate(newActivityManagementDate);
    };

    const handleValidateDates = () => {
        return !!(
            activityManagementDate.referenceStartDate &&
            activityManagementDate.referenceEndDate &&
            activityManagementDate.justificationEndDate &&
            activityManagementDate.approvalEndDate
        );
    };

    const handleValidateReferenceEndDate = () => {
        const convertedDate = moment(selectedDate);

        const convertedReferenceEndDate = moment(
            activityManagementDate.referenceEndDate
        );

        const differenceReferenceEndDate = convertedReferenceEndDate.diff(
            convertedDate,
            "days"
        );

        return differenceReferenceEndDate <= 0;
    };

    const handleValidateReferenceDates = () => {
        const convertedReferenceEndDate = moment(
            activityManagementDate.referenceEndDate
        );

        const differenceReferenceDate = convertedReferenceEndDate.diff(
            activityManagementDate.referenceStartDate,
            "days"
        );

        return differenceReferenceDate >= 0;
    };

    const handleValidateSelectedDate = () => {
        const convertedDate = moment(selectedDate);

        const convertedJustificationEndDate = moment(
            activityManagementDate.justificationEndDate
        );

        const differenceJustificationEndDate =
            convertedJustificationEndDate.diff(convertedDate, "days");

        return differenceJustificationEndDate >= 0;
    };

    const handleValidateApprovalDate = () => {
        const convertedJustificationEndDate = moment(
            activityManagementDate.justificationEndDate
        );

        const convertedApprovalEndDate = moment(
            activityManagementDate.approvalEndDate
        );

        const differenceApprovalEndDate = convertedApprovalEndDate.diff(
            convertedJustificationEndDate,
            "days"
        );

        return differenceApprovalEndDate >= 0;
    };

    const handleIsValidDates = () => {
        const isValidDates = handleValidateDates();
        const isValidReferenceEndDate = handleValidateReferenceEndDate();
        const isValidReferenteDates = handleValidateReferenceDates();
        const isValidSelectedDate = handleValidateSelectedDate();
        const isValidApprovalDate = handleValidateApprovalDate();

        return (
            isValidDates &&
            isValidReferenceEndDate &&
            isValidReferenteDates &&
            isValidSelectedDate &&
            isValidApprovalDate
        );
    };

    const isValid = handleIsValidDates();

    const handleCloseDialogMessage = () => {
        setDialogMessageProps({} as DialogMessageProps);
    };

    const dialogMessage = dialogMessageProps.show && (
        <DialogMessage
            type={dialogMessageProps.type}
            show={dialogMessageProps.show}
            title={dialogMessageProps.title}
            text={dialogMessageProps.text}
            onClose={handleCloseDialogMessage}
        />
    );

    const handleSubmit = async () => {
        if (isValid) {
            const dates = {
                dataSelecionadaNoCalendario: selectedDate,
                periodoInicioReferencia: moment(
                    activityManagementDate.referenceStartDate
                ).format(dateFormat),
                periodoFimReferencia: moment(
                    activityManagementDate.referenceEndDate
                ).format(dateFormat),
                dataLimiteJustificativa: moment(
                    activityManagementDate.justificationEndDate
                ).format(dateFormat),
                dataLimiteAprovacao: moment(
                    activityManagementDate.approvalEndDate
                ).format(dateFormat),
            };

            let onSuccessMessage = "";
            let response = null;

            if (isEditableForm) {
                response =
                    await ActivityManagementDateService.editActivityManagementDate(
                        gestaoAtividadesEdit.id,
                        dates,
                        instance
                    );
                if (GenericUtils.isSuccessStatus(response))
                    onSuccessMessage =
                        "Gestão de Atividades para aprovação de faltas atualizado.";
            } else {
                response =
                    await ActivityManagementDateService.createActivityManagementDate(
                        dates,
                        instance
                    );
                if (GenericUtils.isSuccessStatus(response))
                    onSuccessMessage =
                        "Gestão de Atividades para aprovação de faltas criado.";
            }

            if (GenericUtils.isSuccessStatus(response)) {
                onSuccess(onSuccessMessage);
            } else {
                setDialogMessageProps(
                    GenericUtils.getPropsDialogError(response)
                );
            }
        }
    };

    const handleFillDates = () => {
        const newActivityManagementDate: IActivityManagementDate = {
            referenceStartDate: gestaoAtividadesEdit.referenceStartDate,
            referenceEndDate: gestaoAtividadesEdit.referenceEndDate,
            justificationEndDate: gestaoAtividadesEdit.justificationEndDate,
            approvalEndDate: gestaoAtividadesEdit.approvalEndDate,
        };

        setActivityManagementDate(newActivityManagementDate);
    };

    const referenceStartMinDate = moment(
        activityManagementDate.referenceStartDate
    ).toDate();

    const selectedDateFormat = moment(selectedDate).toDate();

    const approvalMinDate = moment(
        activityManagementDate.justificationEndDate
    ).toDate();

    useEffect(() => {
        if (!handleValidateDates() && isEditableForm) handleFillDates();
    }, []);

    return (
        <>
            <InputCalendarGroup>
                <InputCalendarStyles.StyledCalendarLabel>
                    Início Referência
                </InputCalendarStyles.StyledCalendarLabel>
                <InputCalendarStyles.StyledCalendar
                    name="referenceStartDate"
                    placeholder="Insira uma data ..."
                    dateFormat="dd/mm/yy"
                    locale="pt-BR"
                    autoZIndex
                    touchUI
                    readOnlyInput
                    showButtonBar
                    value={activityManagementDate.referenceStartDate}
                    onChange={(e: any) => handleChangeActivityManagementDate(e)}
                    maxDate={selectedDateFormat}
                />
            </InputCalendarGroup>
            <InputCalendarGroup>
                <InputCalendarStyles.StyledCalendarLabel>
                    Fim Referência
                </InputCalendarStyles.StyledCalendarLabel>
                <InputCalendarStyles.StyledCalendar
                    name="referenceEndDate"
                    placeholder="Insira uma data ..."
                    dateFormat="dd/mm/yy"
                    locale="pt-BR"
                    autoZIndex
                    touchUI
                    readOnlyInput
                    showButtonBar
                    value={activityManagementDate.referenceEndDate}
                    onChange={(e: any) => handleChangeActivityManagementDate(e)}
                    minDate={referenceStartMinDate}
                    maxDate={selectedDateFormat}
                />
            </InputCalendarGroup>
            <InputCalendarGroup>
                <InputCalendarStyles.StyledCalendarLabel>
                    Data limite para justificar a agenda
                </InputCalendarStyles.StyledCalendarLabel>
                <InputCalendarStyles.StyledCalendar
                    name="justificationEndDate"
                    placeholder="Insira uma data ..."
                    dateFormat="dd/mm/yy"
                    locale="pt-BR"
                    autoZIndex
                    touchUI
                    readOnlyInput
                    showButtonBar
                    value={activityManagementDate.justificationEndDate}
                    onChange={(e: any) => handleChangeActivityManagementDate(e)}
                    minDate={selectedDateFormat}
                />
            </InputCalendarGroup>
            <InputCalendarGroup>
                <InputCalendarStyles.StyledCalendarLabel>
                    Data limite para aprovação do workflow
                </InputCalendarStyles.StyledCalendarLabel>
                <InputCalendarStyles.StyledCalendar
                    name="approvalEndDate"
                    placeholder="Insira uma data ..."
                    dateFormat="dd/mm/yy"
                    locale="pt-BR"
                    autoZIndex
                    touchUI
                    readOnlyInput
                    showButtonBar
                    value={activityManagementDate.approvalEndDate}
                    onChange={(e: any) => handleChangeActivityManagementDate(e)}
                    minDate={approvalMinDate}
                />
            </InputCalendarGroup>

            <StyledContainerButton>
                <BtnPrimary disabled={!isValid} onClick={handleSubmit}>
                    Salvar
                </BtnPrimary>
            </StyledContainerButton>
            <StyledContainerButton>
                <DefaultButtonLink onClick={() => setOpenDialogQuestion(true)}>
                    Cancelar
                </DefaultButtonLink>
            </StyledContainerButton>
            {dialogMessage}
        </>
    );
};

export default ModalGestaoAtividadesContent;
